// BOARD-RESULTS WP6 (design 2026-11-02 §5): a research finding's signal at the top of the card's
// details: the signal chip, the trend and its numbers, why it matters and the offer fit, then the
// evidence one line per source with its link. Reads the fields by the names
// @companyos/domain-work `research-signals.ts` defines (the one mapping); nothing renders on a
// card without a Signal or Evidence value.
import { evidenceLines, RESEARCH_SIGNAL_FIELDS, SIGNAL_FIELD } from "@companyos/domain-work/boards";
import { Badge } from "@companyos/ui";

type FieldLite = { id: string; name: string; options?: unknown };

const TONE: Record<string, "destructive" | "warning" | "info" | "secondary"> = {
  urgent: "destructive",
  trending: "warning",
  rising: "info",
  evergreen: "secondary",
};
const ARROW: Record<string, string> = { up: "↑", flat: "→", down: "↓", new: "new" };

const nameOf = (key: string) => RESEARCH_SIGNAL_FIELDS.find((f) => f.key === key)?.name ?? key;

export function ResearchSignalBlock({
  fields,
  values,
}: {
  fields: readonly FieldLite[];
  values: Readonly<Record<string, unknown>>;
}) {
  const v = (key: string): unknown => {
    const f = fields.find((x) => x.name.toLowerCase() === nameOf(key).toLowerCase());
    return f ? values[f.id] : undefined;
  };
  const s = (key: string) => {
    const x = v(key);
    return typeof x === "string" && x.trim() !== "" ? x : null;
  };
  const n = (key: string) => {
    const x = v(key);
    return typeof x === "number" && Number.isFinite(x) ? x : null;
  };
  const signal = s(SIGNAL_FIELD.signal);
  const evidence = evidenceLines(s(SIGNAL_FIELD.evidence));
  if (!signal && evidence.length === 0) return null;
  const direction = s(SIGNAL_FIELD.trendDirection);
  const numbers = [
    n(SIGNAL_FIELD.searchInterest) !== null
      ? `search interest ${n(SIGNAL_FIELD.searchInterest)?.toLocaleString()}`
      : null,
    n(SIGNAL_FIELD.engagement) !== null
      ? `engagement ${n(SIGNAL_FIELD.engagement)?.toLocaleString()}`
      : null,
    n(SIGNAL_FIELD.sources) !== null ? `${n(SIGNAL_FIELD.sources)} sources` : null,
  ].filter(Boolean);
  const why = s(SIGNAL_FIELD.whyItMatters);
  const offer = s(SIGNAL_FIELD.offerFit);
  const expires = s(SIGNAL_FIELD.expires);
  return (
    <section data-slot="research-signal" aria-label="Signal" className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        {signal ? (
          <Badge variant={TONE[signal] ?? "secondary"} data-signal={signal}>
            {signal.charAt(0).toUpperCase() + signal.slice(1)}
          </Badge>
        ) : null}
        {direction ? (
          <span className="text-muted-foreground" title={`Trend: ${direction}`}>
            {ARROW[direction] ?? direction}
          </span>
        ) : null}
        {numbers.length > 0 ? (
          <span className="text-muted-foreground tabular-nums">{numbers.join(" · ")}</span>
        ) : null}
        {expires ? <span className="text-xs text-muted-foreground">until {expires}</span> : null}
      </div>
      {why ? (
        <p className="text-sm">
          <span className="text-muted-foreground">Why it matters: </span>
          {why}
        </p>
      ) : null}
      {offer ? (
        <p className="text-sm">
          <span className="text-muted-foreground">Offer fit: </span>
          {offer}
        </p>
      ) : null}
      {evidence.length > 0 ? (
        <ul className="flex flex-col gap-0.5 text-xs text-muted-foreground" aria-label="Evidence">
          {evidence.map((l, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: lines are positional and static
            <li key={i} className="truncate">
              {l.url ? (
                <a href={l.url} target="_blank" rel="noreferrer" className="underline">
                  {l.text}
                </a>
              ) : (
                l.text
              )}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
